// Accept dropped video files and upload them

import React, { Component, SFC } from 'react'
import { observable, makeObservable } from 'mobx';
import {observer} from 'mobx-react'
import { t } from 'ttag'
import _debug from 'debug'
import { useFlags } from 'launchdarkly-react-client-sdk'

import './Passage.css'
import { displayError, displayInfo } from '../utils/Errors'
import { FfmpegParameters, NativeCompressionInfo, Passage, PassageSegment, PassageVideo } from '../../models3/ProjectModels'
import { Root } from '../../models3/Root'
import DropTarget from '../utils/DropTarget'
import { EditingSegmentPosition } from '../translation/TranslationRightPane'
import { SegmentPositionDialog } from '../segments/SegmentPositionDialog'
import { downloadFcpxml } from '../../finalcutpro/finalcutpro'
import VideoCompressor from '../utils/VideoCompressor'
import { attemptNativeCompression } from '../utils/NativeCompressionRouting'
import { NativeCompressionMetadata } from '../utils/NativeVideoTranscoder'
import { isNativeBrowserVideoCompressionEnabled } from '../utils/NativeCompressionRolloutGate'
import { fmt } from '../utils/Fmt'
import { VideoCacheRecord } from '../../models3/VideoCacheRecord'
import { importEAFFile } from '../../elan/ELANCreator'
import { canUploadWithCompression, canUploadWithoutCompression } from '../images/AllowedVideos'
import { delay } from '../utils/delay'
import { prepNotification } from '../utils/PrepNotification'

function toNativeCompressionInfo(metadata: NativeCompressionMetadata): NativeCompressionInfo {
    let info = new NativeCompressionInfo()
    info.width = metadata.width
    info.height = metadata.height
    info.resolution = metadata.resolution
    info.frameRate = metadata.frameRate
    info.bitrate = metadata.bitrate
    info.codec = metadata.codec
    return info
}

const log = _debug('sltt:VideoDropTarget') 

interface IUploadProgress {
    remaining: number,
}

let UploadProgress: SFC<IUploadProgress> = ({ remaining }) => {
    return (
        <span className="video-upload-progress">
            (<span className="video-upload-bounce fas fa-arrow-up" />
            {remaining.toFixed()})
        </span>
    )
}

interface IVideoDropTarget {
    rt: Root,
    passage: Passage,
    ondone?: () => void,
    videoIsPatch: boolean,
    segment?: PassageSegment,
    dropTargetView: JSX.Element,
}

// Internal-only prop, injected by the VideoDropTarget wrapper component below (see
// nativeBrowserVideoCompression LaunchDarkly flag). Kept separate from IVideoDropTarget so
// existing callers of VideoDropTarget don't need to know about the LaunchDarkly gate.
interface IVideoDropTargetInternal extends IVideoDropTarget {
    nativeBrowserVideoCompressionEnabled: boolean,
}

function displayCompressorError(error: Error, maxVideoSizeMB: number) {
    if (error.name === 'NotEnoughFreeSpace') {
        displayError(/* translator: important */ t`Your hard drive does not have enough free space.`)
    } else if (error.name === 'PayloadTooLarge') {
        displayError(/* translator: important */ t`File upload is too large.`)
    } else if (error.name === 'CompressedFileTooLarge') {
        displayError(t`Could not compress video to less than ${maxVideoSizeMB}MB. Retry with different compression settings.`)
    } else {
        displayError(t`Could not compress video.`)
    }
}

class VideoDropTargetClass extends Component<IVideoDropTargetInternal> {
    @observable percent = 0
    @observable remaining = 0
    @observable editingSegmentPosition = EditingSegmentPosition.None

    constructor(props: IVideoDropTargetInternal) {
        super(props)
        makeObservable(this);
        this.setEditingSegmentPosition = this.setEditingSegmentPosition.bind(this)
    }

    render() {
        let { rt, children, dropTargetView } = this.props
        let { passage, passageVideo, passageSegment } = rt
        let { remaining, upload, editingSegmentPosition } = this

        if (editingSegmentPosition !== EditingSegmentPosition.None) {
            return (
                <SegmentPositionDialog {...{
                    passage: passage!, 
                    video: passageVideo!,
                    segmentIndex: passageVideo!.findSegmentIndex(passageSegment!._id), 
                    editingSegmentPosition,
                    close: (canceled, time, duration) => {
                        if (!canceled) {
                            rt.resetCurrentTime(time, duration)
                        } else {
                            
                        }
                        this.setEditingSegmentPosition(EditingSegmentPosition.None)
                    },
                }} />
            )
        }

        return (
            <DropTarget upload={upload} dropTargetView={dropTargetView}>
                {remaining > 0 && <UploadProgress remaining={remaining} />}
                {children}
            </DropTarget>
        )
    }

    setEditingSegmentPosition(value: EditingSegmentPosition) {
        log('setEditingSegmentPosition', value)
        this.editingSegmentPosition = value
    }

    upload = async (files: FileList, shiftKey: boolean, ctrlKey: boolean) => {
        let { rt, passage } = this.props
        let { iAmTranslator, passageVideo, recording } = rt

        if (!iAmTranslator) {
            displayError(/* translator: important */ t`Only admins or translators can upload videos to project.`)
            return
        }

        if (!passage) {
            displayError(/* translator: important */ t`First select or create a passage for the video.`)
            return
        }

        if (recording) {
            displayError(t`Cannot upload a video while recording.`)
            return
        }

        if (passage.videoBeingCompressed) {
            displayError(t`A video in this passage is already being compressed.`)
            return
        }

        // Files with names starting __video are treated as test files and directy
        // loaded to the video cache
        if (files.length > 0 && files[0].name.startsWith('__video')) {
            for (let file of files) {
                await this.writeTestFileToCache(file)
            }

            return
        }

        let setProgressMessage = (message: string) => {
            passage.compressionProgressMessage = message
        }

        if (files.length !== 1) {
            displayError(t`You must drop exactly one file.`)
            return
        }
        let file = files[0]

        if (file.name.startsWith('_test_comp_')) {
            await this.testCompressionServer(file, setProgressMessage)
            return
        }

        
        // Process Fincal Cut Pro file
        if (file.name.endsWith('.fcpxml')) {
            downloadFcpxml(rt, passage, passageVideo!, file)
                .catch(displayError)
            return
        }

        // Import ELAN gloss file
        if (file.name.endsWith('.eaf')) {
            importEAFFile(rt, passage, passageVideo!, file)
                .catch(displayError)
            return
        }

        if (this.needsCompression(file, shiftKey)) {
            await this.compressAndUpload(file, setProgressMessage)
            return
        }

        let creationDate = new Date() // we no longer use file creation date per Issue #1094
        await this.uploadVideo(file, creationDate)
    }

    needsCompression(file: File, shiftKey: boolean) {
        if (file.name.includes('_force_compression_')) return true

        let sizeMB = file.size / (1024*1024)
        log('needsCompression?', fmt({sizeMB: sizeMB.toFixed(0)}))
        
        if (canUploadWithCompression(file) === '' && canUploadWithoutCompression(file) !== '') return true

        // Use configured maxVideoSizeMB or 256mb is shift key is pressed
        let { maxVideoSizeMB } = this.props.rt.project
        if (shiftKey) {
            maxVideoSizeMB = 256
        }

        return sizeMB > maxVideoSizeMB
    }

    async compressAndUpload(file: File, setProgressMessage: (message: string) => void) {
        log('compressAndUpload start')

        let { rt, nativeBrowserVideoCompressionEnabled } = this.props
        let { maxVideoSizeMB, compressedVideoQuality, compressedVideoResolution } = rt.project
        let creationDate = new Date() // we no longer use file creation date per Issue #1094

        // Gated by the nativeBrowserVideoCompression LaunchDarkly flag (see the VideoDropTarget
        // wrapper below). Flag off (including undefined/not-yet-initialized) skips native
        // entirely and goes straight to the legacy external-compressor flow below, unchanged.
        if (nativeBrowserVideoCompressionEnabled && await this.tryCompressAndUploadNative(file, creationDate, setProgressMessage)) {
            return
        }

        // Native compression was disabled by the flag, unsupported for this file, or failed in a
        // way eligible for fallback (see tryCompressAndUploadNative). Only now do we need the
        // external compressor, so only now do we check whether it is running.
        let isCompressorRunning = await VideoCompressor.checkIfServerRunning()
        if (!isCompressorRunning) { return }

        try {
            while (true) {
                let compressor = new VideoCompressor(compressedVideoQuality, compressedVideoResolution, maxVideoSizeMB, setProgressMessage)
                let { compressedFile, ffmpegParameters } = await compressor.compressVideo(file)
                
                let sizeMB = compressedFile.size/(1024*1024)
                log('compressAndUpload compressVideo', fmt({ sizeMB: sizeMB, compressedVideoResolution, compressedVideoQuality}))

                if (sizeMB <= maxVideoSizeMB) {
                    this.uploadVideo(compressedFile, creationDate, ffmpegParameters)
                    break
                }

                if (compressedVideoResolution > 480) {
                    displayInfo(t`Compressed 720p file too large. Try compressing to 480p.` + ` [${sizeMB.toFixed(0)}mb]`)
                    compressedVideoResolution = 480
                    continue
                }

                if (compressedVideoQuality < 28) {
                    compressedVideoQuality = Math.min(compressedVideoQuality+3, 28)
                    displayInfo(t`Compressed file still too large, trying again at lower quality.` + ` [${sizeMB.toFixed(0)}mb, crf=${compressedVideoQuality}]`)
                    continue
                }

                displayError(t`Compressed video still too large, upload failed.`)
                break
            }
        } catch (error) {
            let {name, message} = error as Error
            log('compressAndUpload ERROR', fmt({name, message}))

            setProgressMessage('')
            displayCompressorError(error as Error, maxVideoSizeMB)
        }
    }

    // Tries to compress and upload `file` entirely in-browser via WebCodecs, without ever
    // contacting the external compressor. Returns true if a native result was (or is being)
    // uploaded, so the caller should not fall back. Returns false when native compression is
    // unsupported for this file or failed in a way eligible for fallback (native-unsupported,
    // native-decode/encode/mux failure, or native size-retry exhaustion — see
    // NativeVideoTranscoder.ts), so the caller should fall back to the external compressor.
    // The actual native-vs-fallback decision lives in attemptNativeCompression (a plain,
    // non-React module) so it can be unit-tested directly.
    async tryCompressAndUploadNative(file: File, creationDate: Date, setProgressMessage: (message: string) => void): Promise<boolean> {
        let { rt } = this.props
        let { maxVideoSizeMB, compressedVideoResolution } = rt.project

        let outcome = await attemptNativeCompression(file, maxVideoSizeMB, compressedVideoResolution, setProgressMessage)
        if (!outcome.succeeded) {
            log('tryCompressAndUploadNative: native compression unsupported or failed, falling back')
            return false
        }

        log('tryCompressAndUploadNative succeeded', fmt({ sizeMB: outcome.file.size / (1024 * 1024), metadata: outcome.metadata }))
        setProgressMessage(t`Preparing upload...`)
        try {
            // Awaited (unlike the legacy compressor path, where VideoCompressor.downloadFile()
            // already clears compressionProgressMessage before compressVideo() even returns, so
            // uploadVideo() is fire-and-forget safely there) so the finally below has a real
            // completion signal to clear "Preparing upload..." on — otherwise nothing ever clears
            // it and passage.videoBeingCompressed stays stuck true. uploadVideo() itself never
            // rejects (it catches and displays its own errors internally), so this is not a new
            // failure path — it exists purely to guarantee a deterministic exit from this message.
            //
            // onLocalPreparationComplete clears the message earlier still, as soon as the new
            // video is actually persisted locally (VideoCache + PassageVideo record) and about to
            // be activated: passage.videoBeingCompressed derives from this message, and
            // Root.setPassageVideo()/setPassage() silently no-op while it's true, so leaving it
            // set until uploadVideo() fully returns would make the setPassage/setPassageVideo
            // calls inside uploadFile()/uploadSegmentPatchFile() below silently do nothing — the
            // new video would exist but never become the active, rendered PassageVideo. The
            // finally block below remains as an idempotent safety net for paths (or failures)
            // that never reach that callback.
            await this.uploadVideo(
                outcome.file, creationDate, undefined, toNativeCompressionInfo(outcome.metadata),
                () => setProgressMessage('')
            )
        } finally {
            setProgressMessage('')
        }
        return true
    }

    async uploadVideo(
        file: File, creationDate: Date, ffmpegParametersUsed?: FfmpegParameters,
        nativeCompressionInfo?: NativeCompressionInfo, onLocalPreparationComplete?: () => void
    ) {
        try {
            let { rt, videoIsPatch, segment } = this.props
            let { maxVideoSizeMB } = rt.project
            this.validateFile(file, maxVideoSizeMB)
            let symbol = '❚⬆️'
            let english = 'Patch was uploaded'
            let native = t`Patch was uploaded`
            let video: PassageVideo | undefined = undefined
            if (rt.selectionPresent()) {
                video = await this.uploadSelectionPatchFile(file, ffmpegParametersUsed, nativeCompressionInfo, onLocalPreparationComplete)
            } else if (videoIsPatch) {
                video = await this.uploadSegmentPatchFile(file, segment, ffmpegParametersUsed, nativeCompressionInfo, onLocalPreparationComplete)
            } else {
                video = await this.uploadFile(file, creationDate, ffmpegParametersUsed, nativeCompressionInfo, onLocalPreparationComplete)
                symbol = '⬆️'
                english = 'Video was uploaded to a passage'
                native = t`Video was uploaded to a passage`
            }
            video && prepNotification({
                linkTarget: video, rt, eventSummary: { symbol, english, native },
                creationDate: video.creationDate,
            })
        } catch (err) {
            displayError(err)
        }

        this.remaining = 0
    }

    validateFile(file: File, maxFileSizeMB: number) {
        if (file.size > maxFileSizeMB*1024*1024) {
            throw new Error(/* translator: important */ t`File size too large, greater than ${maxFileSizeMB}M.`)
        }

        const errorMessage = canUploadWithCompression(file)
        if (errorMessage) {
            throw new Error(errorMessage)
        }
    }

    async testCompressionServer(file: File, setProgressMessage: (message: string) => void) {
        let isCompressorRunning = await VideoCompressor.checkIfServerRunning()
        if (!isCompressorRunning) { return }

        let { maxVideoSizeMB, compressedVideoResolution, compressedVideoQuality } = this.props.rt.project

        try {
            let compressor = new VideoCompressor(compressedVideoQuality, compressedVideoResolution, maxVideoSizeMB, setProgressMessage)
            let compressedData = await compressor.compressVideo(file)
            
            // Download compressed video file
            let href = window.URL.createObjectURL(compressedData.compressedFile)
            let link = document.createElement('a')
            link.setAttribute('href', href)
            link.setAttribute('download', 'compressed-video.mp4')
            link.click()
        } catch (error) {
            setProgressMessage('')
            displayError((error as Error).name)
        }
    }

    /* Check if selection is patchable.
     * If so, create a segment for the selection and upload a patch file to that segment.
     */
    async uploadSelectionPatchFile(
        file: File, ffmpegParametersUsed?: FfmpegParameters, nativeCompressionInfo?: NativeCompressionInfo,
        onLocalPreparationComplete?: () => void
    ) {
        let { rt } = this.props
        let { passage, passageVideo, selectionStartTime, selectionEndTime } = rt
        if (!passage || !passageVideo) return undefined

        if (!rt.patchableSelectionPresent(displayError)) { return undefined }

        passageVideo.log(passage, 'before createSelectionSegment')

        let { segment } = await passageVideo.createSelectionSegment(passage, selectionStartTime, selectionEndTime)
        rt.setPassageSegment(segment) // set current segment to newly created segment
        passageVideo.log(passage, 'after createSelectionSegment')

        return await this.uploadSegmentPatchFile(file, segment, ffmpegParametersUsed, nativeCompressionInfo, onLocalPreparationComplete)
    }

    async uploadSegmentPatchFile(
        file: File, segment?: PassageSegment, ffmpegParametersUsed?: FfmpegParameters,
        nativeCompressionInfo?: NativeCompressionInfo, onLocalPreparationComplete?: () => void
    ) {
        let { rt, passage, ondone } = this.props

        if (!segment) { throw new Error('Something went wrong. No segment for patch.') }

        let { passageVideo, name, portion } = rt
        if (!passageVideo || !portion) return

        let _onprogress = (remaining: number) => { this.remaining = remaining }

        // Always use todays date for patches. If we allow older dates for patch files
        // the patch may be excuded by VisiblePassageVideo later.
        let creationDate = new Date()
        let patch = await passage.uploadFile(file, creationDate, name, portion.name, _onprogress)

        if (ffmpegParametersUsed !== undefined) {
            patch.ffmpegParametersUsed = ffmpegParametersUsed
        }

        if (nativeCompressionInfo !== undefined) {
            patch.nativeCompressionInfo = nativeCompressionInfo
        }

        const newPatchVideo = await passage.addPatchVideo(passageVideo, patch, segment)
        passageVideo.log(passage, 'after addPatchVideo')

        // The patch is now fully persisted locally (passage.uploadFile above already wrote it
        // into VideoCache). This is the safe point to release a compression-in-progress guard
        // (see the identical reasoning in tryCompressAndUploadNative) before setPassageVideo,
        // which Root would otherwise silently ignore while that guard is still active.
        onLocalPreparationComplete?.()

        await rt.setPassageVideo(passageVideo)
        rt.setPassageSegment(segment)
        await this.waitForVideoElementsToLoad()

        let actualSegment = segment.actualSegment(passage)
        rt.resetCurrentTime(actualSegment.time + 0.01)

        ondone?.()
        setTimeout(() => { this.remaining = 0 }, 2000)

        this.setEditingSegmentPosition(EditingSegmentPosition.Both)
        return newPatchVideo
    }

    async writeTestFileToCache(file: File) {
        let _id = file.name.slice(2, -4) + '-1'
        log('writeTestFileToCache', _id)
        
        let vcr = await VideoCacheRecord.get(_id)
        await vcr.deleteBlobs() // if existing video data for this file, remove it
        vcr.size = file.size
        await vcr.addBlob(file)
        
        log('!!!writeTestFileToCache DONE', _id)
    }
    
    async uploadFile(
        file: File, creationDate: Date, ffmpegParametersUsed?: FfmpegParameters,
        nativeCompressionInfo?: NativeCompressionInfo, onLocalPreparationComplete?: () => void
    ) {
        let _onprogress = (remaining: number) => {
            this.remaining = remaining
        }

        let { rt, passage } = this.props

        let { name, portion } = rt
        let video = await passage.uploadFile(file, creationDate, name, portion!.name, _onprogress)

        if (ffmpegParametersUsed !== undefined) {
            video.ffmpegParametersUsed = ffmpegParametersUsed
        }

        if (nativeCompressionInfo !== undefined) {
            video.nativeCompressionInfo = nativeCompressionInfo
        }

        // This will indirectly trigger uploading the video blob in the cache to S3
        // when DBAcceptor calls VideoCache.acceptPassageVideo
        await passage.addVideoWithDefaultSegment(video)
        let _video = passage.videos.find(v => v._id === video._id)

        // Local preparation (the compressed file is in VideoCache, the PassageVideo and its
        // default segment are persisted) is complete: this is the safe point to release a
        // compression-in-progress guard, if the caller is tracking one, before the
        // setPassage/setPassageVideo calls below. Root silently no-ops those calls while
        // project.videoBeingCompressed is true, so releasing the guard any later would leave the
        // newly uploaded video created but never activated/rendered.
        onLocalPreparationComplete?.()

        await rt.setPassage(passage)

        // We set passageVideo to null first in order to ensure that
        // VideoMain will update. Otherwise there is a race condition
        // where DBAcceptor partially set up the passageVideo and triggers
        // a VideoMain render before the passageVideo has all the info
        // to correctly display.
        await rt.setPassageVideo(null)
        await rt.setPassageVideo(_video!)

        setTimeout(() => { this.remaining = 0 }, 2000)
        return _video
    }

    async waitForVideoElementsToLoad() {
        while (true) {
            if (this.props.rt.currentVideos.downloaded) return
            await delay(50)
        }
    }
}

const ObservedVideoDropTarget = observer(VideoDropTargetClass)

// Thin functional wrapper: VideoDropTargetClass is a MobX-observed class component and cannot
// call hooks directly, so this is the smallest way to read the nativeBrowserVideoCompression
// LaunchDarkly flag (following this repo's existing useFlags() convention — see
// TranslationRightPane.tsx) and inject it as an internal-only boolean prop. Public props for
// existing callers (IVideoDropTarget) are unchanged.
const VideoDropTarget: SFC<IVideoDropTarget> = (props) => {
    const { nativeBrowserVideoCompression } = useFlags()
    const nativeBrowserVideoCompressionEnabled = isNativeBrowserVideoCompressionEnabled(nativeBrowserVideoCompression)

    return <ObservedVideoDropTarget {...props} nativeBrowserVideoCompressionEnabled={nativeBrowserVideoCompressionEnabled} />
}

export default VideoDropTarget